// utils/tagRules.js
//
// AUTOMATIC TAGS, the client's pure half (docs/plans/tags.md §3.4, §5.1,
// §5.2; keepr-api docs/SCHEMA.md § Automatic tags, utils/tagRules.js). An
// automatic tag is a collection tag with a rule; the rule's editor
// (components/tags/TagRuleEditor), the tag manager's row, the read view's
// "Why" pop-over and History all read these, so no surface restates a rule.
// No React, no network.
//
// The rule on the wire (what GET answers and PUT takes):
//
//   { card_id, where: 'token-free KQL on the item' | null, match: 'all' | 'any',
//     related: [ { card_id, link, filter | null, window: { field, unit, every } | null,
//                  aggregate, of | null, op, value, whenNone } ],   // 0–3
//     strict }
//
// The EDITOR works on a draft of the same shape, with a `key` per related
// clause (React's) and empty strings where the wire says null; `ruleFromDraft`
// turns it back into exactly what the API takes — empty optional keys left
// out, which the API derives (`where`, a clause's `filter` / `window` / `of`).
//
// The API judges every rule again (its own validator, `invalid_rule` with the
// `field` it refused); the helpers below only say, before anyone clicks, what
// the API is known to refuse — and put its refusal where it belongs.

import dayjs from 'dayjs';

import { formatDisplayDate, formatDisplayDateTime } from './dateDisplay';
import { formatCurrency, isCurrencyValue } from './currency';
import { defaultUnitFor, formatEnteredMeasurement } from './measurements';
import { getUnit, isMeasurementValue } from './units';
import { normalizeId } from './ids';

/** The rule's limits, mirrored from keepr-api utils/tagRules.js so the editor can say them first. */
export const RULE_LIMITS = Object.freeze({
	related: 3,
	every: 400,
	textValue: 500,
	previewScan: 200,
	handTags: 50,
});

export const MATCHES = Object.freeze(['all', 'any']);
export const AGGREGATES = Object.freeze(['latest', 'count', 'sum', 'min', 'max', 'average']);
/** The aggregates that need a number, measurement or money element (the API's NUMERIC_AGGREGATES). */
export const NUMERIC_AGGREGATES = new Set(['sum', 'min', 'max', 'average']);
export const DATE_OPS = new Set(['olderThan', 'withinLast', 'withinNext']);
const NUMERIC_TYPES = new Set(['number', 'decimal', 'integer', 'rating']);
const DATE_TYPES = new Set(['date', 'date-time']);
export const DURATION_UNITS = Object.freeze(['minute', 'hour', 'day', 'week']);
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** The two timestamps every item has, as a related clause's "of" (the API's SYSTEM_TOKENS). */
export const SYSTEM_SOURCES = Object.freeze([
	{ name: '$createdAt', label: 'When it was created', dataType: 'date-time', system: true },
	{ name: '$updatedAt', label: 'When it was last saved', dataType: 'date-time', system: true },
]);

/** A window measures from a date of the related item, or one of its two timestamps (no `$` there). */
export const WINDOW_SYSTEM_FIELDS = Object.freeze([
	{ value: 'createdAt', label: 'When it was created' },
	{ value: 'updatedAt', label: 'When it was last saved' },
]);

// ------------------------------------------------------------------ drafts

let clauseSeq = 0;
const nextKey = () => `clause-${++clauseSeq}`;

const text = value => (typeof value === 'string' ? value : '');

/** A new related clause: nothing chosen yet; "when there is none" does not count. */
export function emptyClause() {
	return {
		key: nextKey(),
		card_id: '',
		link: '',
		filter: '',
		window: null,
		aggregate: 'latest',
		of: '',
		op: '',
		value: null,
		whenNone: false,
	};
}

/**
 * The editor's draft from the rule GET answered (null for a hand tag: a new
 * rule, non-strict by default — ruled d11b).
 */
export function draftFromRule(rule) {
	if (!rule) {
		return { card_id: '', where: '', match: 'all', related: [], strict: false };
	}
	return {
		card_id: normalizeId(rule.card_id) || '',
		where: text(rule.where),
		match: rule.match === 'any' ? 'any' : 'all',
		related: (Array.isArray(rule.related) ? rule.related : []).map(clause => ({
			key: nextKey(),
			card_id: normalizeId(clause?.card_id) || '',
			link: text(clause?.link),
			filter: text(clause?.filter),
			window: clause?.window && typeof clause.window === 'object'
				? { field: text(clause.window.field), unit: clause.window.unit || 'day', every: Number(clause.window.every) || 1 }
				: null,
			aggregate: AGGREGATES.includes(clause?.aggregate) ? clause.aggregate : 'latest',
			of: text(clause?.of),
			op: text(clause?.op),
			value: clause?.value === undefined ? null : clause.value,
			whenNone: clause?.whenNone === true,
		})),
		strict: rule.strict === true,
	};
}

/**
 * The draft as the API takes it. Empty optional keys are left out (the API
 * derives them); a count has no "of"; a window without its date is none.
 */
export function ruleFromDraft(draft) {
	const where = text(draft?.where).trim();
	const related = (Array.isArray(draft?.related) ? draft.related : []).map(clause => {
		const out = {
			card_id: clause.card_id,
			link: clause.link,
			aggregate: clause.aggregate,
		};
		const filter = text(clause.filter).trim();
		if (filter) out.filter = filter;
		if (clause.window && clause.window.field) {
			out.window = { field: clause.window.field, unit: clause.window.unit || 'day', every: Number(clause.window.every) || 1 };
		}
		if (clause.aggregate !== 'count' && clause.of) out.of = clause.of;
		out.op = clause.op;
		out.value = clause.value;
		out.whenNone = clause.whenNone === true;
		return out;
	});
	return {
		card_id: draft?.card_id || '',
		...(where ? { where } : {}),
		match: draft?.match === 'any' ? 'any' : 'all',
		related,
		strict: draft?.strict === true,
	};
}

/** What a rule MEANS, strict aside — the API's own "did it change" (ruleHash). */
function meaningOf(rule) {
	const wire = ruleFromDraft(draftFromRule(rule));
	delete wire.strict;
	return JSON.stringify(wire);
}

/** Is the draft the stored rule, unchanged? (`stored` as GET answered it, or null.) */
export function sameRule(stored, draft) {
	if (!stored) return false;
	const next = ruleFromDraft(draft);
	return meaningOf(stored) === meaningOf(next) && (stored.strict === true) === next.strict;
}

/** How many parts the rule has: its filter, and each related clause. */
export const partCount = draft => (text(draft?.where).trim() ? 1 : 0) + (Array.isArray(draft?.related) ? draft.related.length : 0);

// ------------------------------------------------------------------ cards and elements

const labelOf = element => element?.label?.singular || element?.name || '';
export const cardSingular = card => card?.label?.singular || card?.name || 'related item';
export const cardPlural = card => card?.label?.plural || (card?.name ? `${card.name}s` : 'related items');

/** A card and every card above it (parentCardId), as ids: what a lookup may point at to reach it. */
export function cardChainIds(cardId, cardsById = {}) {
	const ids = new Set();
	let cursor = normalizeId(cardId);
	while (cursor && !ids.has(cursor)) {
		ids.add(cursor);
		cursor = normalizeId(cardsById?.[cursor]?.parentCardId);
	}
	return ids;
}

/** The item lookups on a card's elements that point at one of `chain` (the rule's card, or a card above it). */
export function linkElementsTo(elements = [], chain = new Set()) {
	return (elements || []).filter(element => element?.name
		&& element.dataType === 'card-lookup'
		&& chain.has(normalizeId(element?.options?.lookupCardId)));
}

/**
 * The cards whose items can relate to the rule's card: each with an item
 * lookup pointing at it (or a card above it) — `[{ card, links }]`, in the
 * order given. A card with no such lookup cannot be a related card (the API
 * refuses `related[i].link`), so it is not offered.
 */
export function relatedCardsFor(ruleCardId, cards = [], getElementsForCard, cardsById = {}) {
	if (!ruleCardId) return [];
	const chain = cardChainIds(ruleCardId, cardsById);
	return (cards || []).map(card => {
		const elements = typeof getElementsForCard === 'function' ? (getElementsForCard(normalizeId(card?._id)) || []) : (card?.elements || []);
		return { card, links: linkElementsTo(elements, chain) };
	}).filter(entry => entry.links.length);
}

/**
 * The elements a clause may aggregate, for its aggregate: `latest` any
 * element (and the two timestamps); sum, min, max and average a number,
 * measurement or money element; a count none.
 */
export function sourceElementsFor(elements = [], aggregate = 'latest') {
	if (aggregate === 'count') return [];
	const own = (elements || []).filter(element => element?.name);
	if (NUMERIC_AGGREGATES.has(aggregate)) {
		return own.filter(element => NUMERIC_TYPES.has(element.dataType) || element.dataType === 'measurement' || element.dataType === 'currency');
	}
	return [...own, ...SYSTEM_SOURCES];
}

/** The date elements a window may measure from. */
export const windowElementsOf = (elements = []) => (elements || []).filter(element => element?.name && DATE_TYPES.has(element.dataType));

/** The element a clause's "of" names (a timestamp counts as a date-time), or null. */
export function clauseSource(clause, elements = []) {
	if (!clause || clause.aggregate === 'count') return null;
	const system = SYSTEM_SOURCES.find(source => source.name === clause.of);
	if (system) return system;
	return (elements || []).find(element => element?.name === clause.of) || null;
}

/**
 * What the clause compares, by kind — `'number' | 'date' | 'measurement' |
 * 'currency' | 'boolean' | 'text'` — or null until "of" is chosen. A count
 * is a number; a numeric aggregate keeps its element's kind (the API's
 * outType).
 */
export function clauseValueKind(clause, elements = []) {
	if (!clause) return null;
	if (clause.aggregate === 'count') return 'number';
	const source = clauseSource(clause, elements);
	if (!source) return null;
	const type = source.dataType;
	if (DATE_TYPES.has(type)) return 'date';
	if (NUMERIC_TYPES.has(type)) return 'number';
	if (type === 'measurement') return 'measurement';
	if (type === 'currency') return 'currency';
	if (type === 'boolean') return 'boolean';
	return 'text';
}

const OP_LABELS = {
	date: [
		['olderThan', 'is older than'], ['withinLast', 'is within the last'], ['withinNext', 'is within the next'],
		['lt', 'is before'], ['lte', 'is on or before'], ['gt', 'is after'], ['gte', 'is on or after'],
		['eq', 'is on'], ['ne', 'is not on'],
	],
	number: [
		['gt', 'is more than'], ['gte', 'is at least'], ['lt', 'is less than'], ['lte', 'is at most'],
		['eq', 'is'], ['ne', 'is not'],
	],
	boolean: [['eq', 'is'], ['ne', 'is not']],
	text: [['eq', 'is'], ['ne', 'is not']],
};
OP_LABELS.measurement = OP_LABELS.number;
OP_LABELS.currency = OP_LABELS.number;

/** The comparisons a kind of value takes, as Select options. */
export function opsForKind(kind) {
	return (OP_LABELS[kind] || []).map(([value, label]) => ({ value, label }));
}

/** Is `op` one this kind takes? (A changed "of" may leave a stale one behind.) */
export const opFitsKind = (op, kind) => (OP_LABELS[kind] || []).some(([value]) => value === op);

/** The value a clause starts with for its comparison (a date operator's is a span of days). */
export function defaultValueFor(kind, op) {
	if (DATE_OPS.has(op)) return { every: 30, unit: 'day' };
	if (kind === 'boolean') return true;
	if (kind === 'date' || kind === 'text') return '';
	return null;
}

/** "Whether there is none" matters only where a blank is possible: a count and a sum compare their zero. */
export const whenNoneApplies = clause => Boolean(clause) && clause.aggregate !== 'count' && clause.aggregate !== 'sum';

/**
 * What is still missing before the draft can be saved or previewed, as the
 * words a person reads — or null when nothing is. The API is the judge; these
 * are the gaps any person would see.
 */
export function draftGap(draft, { elementsFor } = {}) {
	if (!draft?.card_id) return 'Choose the card whose items this rule tags.';
	const related = Array.isArray(draft.related) ? draft.related : [];
	for (let i = 0; i < related.length; i++) {
		const clause = related[i];
		const n = related.length > 1 ? ` ${i + 1}` : '';
		if (!clause.card_id) return `Related items${n}: choose their card.`;
		if (!clause.link) return `Related items${n}: choose the item lookup that points at this card.`;
		const elements = typeof elementsFor === 'function' ? elementsFor(clause.card_id) : [];
		if (clause.aggregate !== 'count' && !clause.of) return `Related items${n}: choose what to take.`;
		const kind = clauseValueKind(clause, elements);
		if (!clause.op || (kind && !opFitsKind(clause.op, kind))) return `Related items${n}: choose how to compare it.`;
		if (DATE_OPS.has(clause.op)) {
			const every = Number(clause.value?.every);
			if (!Number.isInteger(every) || every < 1 || every > RULE_LIMITS.every) return `Related items${n}: give a whole number from 1 to ${RULE_LIMITS.every}.`;
		} else if (kind === 'date') {
			if (!text(clause.value).trim()) return `Related items${n}: choose the date to compare with.`;
		} else if (kind === 'number' || kind === 'measurement' || kind === 'currency') {
			if (typeof clause.value !== 'number' || !Number.isFinite(clause.value)) return `Related items${n}: give the number to compare with.`;
		} else if (kind === 'boolean') {
			if (typeof clause.value !== 'boolean') return `Related items${n}: choose yes or no.`;
		} else if (kind === 'text' && typeof clause.value !== 'string') {
			return `Related items${n}: give the text to compare with.`;
		}
	}
	return null;
}

// ------------------------------------------------------------------ words: values

const plural = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;

/** A count as words, zero included: "no items", "1 item", "1,200 items". */
export function countWords(n, one, many) {
	const value = Number(n) || 0;
	const several = many || `${one}s`;
	if (value <= 0) return `no ${several}`;
	return `${value.toLocaleString()} ${value === 1 ? one : several}`;
}

/** "180 days", "1 week". */
export function durationWords(value) {
	const every = Number(value?.every) || 0;
	const unit = DURATION_UNITS.includes(value?.unit) ? value.unit : 'day';
	return plural(every, unit);
}

const isIsoDate = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value.trim());

/**
 * A value as the words a person reads: a date through the display preference,
 * a measurement and money as they are written, yes / no, a number with its
 * separators; anything else as text.
 */
export function valueWords(value, { kind = null, element = null } = {}) {
	if (value === null || value === undefined || value === '') return '';
	if (typeof value === 'boolean') {
		const options = element?.options || {};
		if (value) return (typeof options.trueLabel === 'string' && options.trueLabel.trim()) || 'Yes';
		return (typeof options.falseLabel === 'string' && options.falseLabel.trim()) || 'No';
	}
	// An element's own unit (KPR-1109) is named by the element's options.
	if (isMeasurementValue(value)) return formatEnteredMeasurement(value, element?.options) || '';
	if (isCurrencyValue(value)) return formatCurrency(value) || '';
	if (kind === 'date' || isIsoDate(value)) {
		const s = `${value}`.trim();
		return DATE_ONLY.test(s) ? formatDisplayDate(s) : formatDisplayDateTime(s);
	}
	if (typeof value === 'number') {
		const base = value.toLocaleString();
		if (kind === 'measurement') {
			const unit = getUnit(defaultUnitFor(element?.options || {}));
			return unit?.symbol ? `${base} ${unit.symbol}` : base;
		}
		if (kind === 'currency') {
			const code = element?.options?.defaultCurrency;
			return code ? `${base} ${code}` : base;
		}
		return base;
	}
	if (typeof value === 'object') return '';
	return `${value}`;
}

// ------------------------------------------------------------------ words: clauses and facts

/** Who a clause reads: "Latest Oil change", "Total Amount of Orders", "Number of Visits". */
export function clauseSubject(clause, { card = null, elements = [] } = {}) {
	const one = cardSingular(card);
	const many = cardPlural(card);
	const source = clauseSource(clause, elements);
	const what = source?.system ? null : labelOf(source) || clause?.of || 'value';
	switch (clause?.aggregate) {
		case 'count': return `Number of ${many}`;
		case 'sum': return `Total ${what} of ${many}`;
		case 'min': return `Smallest ${what} of ${many}`;
		case 'max': return `Largest ${what} of ${many}`;
		case 'average': return `Average ${what} of ${many}`;
		default: {
			// The latest one's DATE is the item itself ("Latest Oil change is
			// older than 180 days"); any other element is named.
			const dated = !source || source.system || DATE_TYPES.has(source.dataType);
			return dated ? `Latest ${one}` : `${what} of the latest ${one}`;
		}
	}
}

/** "is older than 180 days", "is more than 2,000", "is Yes". */
export function comparisonWords(clause, { kind = null, element = null } = {}) {
	const op = clause?.op;
	if (DATE_OPS.has(op)) {
		const span = durationWords(clause.value);
		if (op === 'olderThan') return `is older than ${span}`;
		if (op === 'withinLast') return `is within the last ${span}`;
		return `is within the next ${span}`;
	}
	const label = (OP_LABELS[kind || 'number'] || OP_LABELS.number).find(([value]) => value === op)?.[1] || 'is';
	const shown = valueWords(clause?.value, { kind, element });
	if (kind === 'text') return `${label} “${shown}”`;
	return shown ? `${label} ${shown}` : label;
}

/**
 * One related clause as a sentence (the editor's heading, and the rule in
 * words): "Latest Oil change is older than 180 days", "Number of Visits in
 * the last 30 days is 0".
 */
export function describeClause(clause, { card = null, elements = [] } = {}) {
	if (!clause?.card_id) return '';
	const kind = clauseValueKind(clause, elements);
	const element = clauseSource(clause, elements);
	let subject = clauseSubject(clause, { card, elements });
	if (clause.window?.field) subject = `${subject} in the last ${durationWords(clause.window)}`;
	if (!clause.op || !kind) return subject;
	return `${subject} ${comparisonWords(clause, { kind, element })}`;
}

const ACTUAL_WORDS = { latest: 'last', count: 'counted', sum: 'total', min: 'smallest', max: 'largest', average: 'average' };

/**
 * One FACT of a reason (GET /api/items/{id}/tag-reasons) as a sentence:
 * "Latest Oil change is older than 180 days (last: 2 Mar 2026)". A fact whose
 * aggregate was blank reads as "There is no Oil change yet", and says the
 * rule counts that as a match. The related item it names, when the reader
 * may read it, is the surface's to draw (a record badge), not a word here.
 */
export function describeFact(fact, { card = null, elements = [] } = {}) {
	if (!fact) return '';
	const clause = { aggregate: fact.aggregate, of: fact.of || '', op: fact.op, value: fact.value, card_id: fact.card_id };
	const kind = clauseValueKind(clause, elements) || (DATE_OPS.has(fact.op) ? 'date' : null);
	const element = clauseSource(clause, elements);
	if (fact.none) {
		return `There is no ${cardSingular(card)} with a value yet, and the rule counts that as a match.`;
	}
	const sentence = `${clauseSubject(clause, { card, elements })} ${comparisonWords(clause, { kind, element })}`;
	const actual = valueWords(fact.actual, { kind: DATE_OPS.has(fact.op) ? 'date' : kind, element });
	return actual ? `${sentence} (${ACTUAL_WORDS[fact.aggregate] || 'now'}: ${actual}).` : `${sentence}.`;
}

// ------------------------------------------------------------------ previews and passes

/**
 * A preview (POST …/rule/preview, or a dry run's `preview`) as the sentence
 * a person reads — and whether it covered every item. A rule with related
 * clauses is evaluated over the first 200 items of its card; the rest are
 * checked when it is saved.
 */
export function previewWords(preview) {
	if (!preview) return '';
	const total = Number(preview.total) || 0;
	const matching = Number(preview.matching) || 0;
	const scanned = Number(preview.scanned) || 0;
	if (!total) return 'This card has no items here yet, so the rule would tag nothing for now.';
	// A rule with related clauses is evaluated over the first 200 items of
	// its card, and stops early after 10 s or 20,000 related items read
	// (keepr-api T6a review fix): `complete: false` whenever it did not see
	// them all.
	if (preview.complete === false) {
		return `The preview is incomplete: it checked ${scanned.toLocaleString()} of ${countWords(total, 'item')}, and ${matching.toLocaleString()} of them would get this tag. The rest are checked when you save.`;
	}
	return matching
		? `${countWords(matching, 'item')} of ${total.toLocaleString()} would get this tag.`
		: `None of the ${countWords(total, 'item')} would get this tag.`;
}

const PASS_KINDS = { 'tagRule.sync': 'sync', 'tagRule.pause': 'pause', 'tagRule.stop': 'stop' };

/** A pass's kind: 'sync' (a rule saved or resumed), 'pause' or 'stop'. */
export const passKindOf = pass => PASS_KINDS[pass?.kind] || 'sync';

/** Is this a "Stop applying by rule" pass that has not finished (still running, waiting, failed or cut short)? */
export const unfinishedStop = pass => Boolean(pass) && passKindOf(pass) === 'stop' && pass.status !== 'done';

/** The item ids a pass listed as full (they already carried 50 tags) — the first 20 (keepr-api T6b; the rest are counted). */
export const passFullIds = pass => (Array.isArray(pass?.progress?.full) ? pass.progress.full.map(normalizeId).filter(Boolean) : []);

// Why a live pass cannot run yet (`pass.waiting`, keepr-api T6a review fix):
// it stays live, and the server asks again every ten minutes.
const WAITING_WORDS = {
	collection_archived: 'the collection, or a sub-collection holding items with this tag, is archived',
};

/**
 * Where a rule's pass stands, in words: `{ kind, running, waiting, tone, text,
 * full, fullCount }`. `tone` is 'info' while it runs, 'warning' while it waits
 * or when it stopped early or left items out, 'success' when done, 'error'
 * when it failed. A pass whose world went away under it (the rule paused or
 * reshaped, the tag deleted) ends `cancelled` with `cancelledReason: 'stale:
 * …'`; a pause or a stop that cannot run yet stays `running` with `waiting`.
 */
export function passState(pass) {
	if (!pass) return null;
	const kind = passKindOf(pass);
	const p = pass.progress || {};
	const n = key => Number(p[key]) || 0;
	const running = pass.running === true;
	const fullCount = n('fullCount') || passFullIds(pass).length;
	const full = passFullIds(pass);
	if (pass.status === 'failed') {
		// Only a sync fails for good (a pause or a stop keeps retrying); items
		// are still checked as they change, and resuming runs a new sync.
		const text = kind === 'sync'
			? 'The rule stopped partway with an error. Items are still checked as they change; pause and resume it to check every item again.'
			: 'This stopped partway with an error. keepr tries again on its own.';
		return { kind, running: false, tone: 'error', text, full, fullCount };
	}
	if (running && pass.waiting) {
		const why = WAITING_WORDS[pass.waiting] || 'it can’t run yet';
		const verb = kind === 'sync' ? 'Applying the rule' : (kind === 'pause' ? 'Pausing the rule' : 'Stopping the rule');
		return { kind, running: true, waiting: pass.waiting, tone: 'warning', text: `${verb} is waiting: ${why}. keepr tries again every ten minutes.`, full, fullCount };
	}
	const parts = [];
	if (n('handRemoved')) parts.push(`took ${countWords(n('handRemoved'), 'hand application')} off`);
	if (kind === 'sync') {
		if (n('applied')) parts.push(`tagged ${countWords(n('applied'), 'item')}`);
		if (n('removed')) parts.push(`took it off ${countWords(n('removed'), 'item')}`);
	} else if (kind === 'pause') {
		if (n('removed')) parts.push(`took it off ${countWords(n('removed'), 'item')}`);
	} else {
		if (n('movedToHand')) parts.push(`${countWords(n('movedToHand'), 'automatic application')} became hand ${n('movedToHand') === 1 ? 'one' : 'ones'}`);
		if (n('removed')) parts.push(`${countWords(n('removed'), 'item')} already had it by hand`);
	}
	const joined = parts.join(', ');
	const stopped = pass.status === 'cancelled' || p.stopped === true || p.stale === true || /^stale/.test(pass.cancelledReason || '');
	let textOut;
	if (running) {
		// A share once the pass knows how many items it started with (T6b
		// `progress.total`); a nudge's second look or items written since can
		// take `scanned` past it, and then it is only counted.
		const total = n('total');
		const share = total > 0 && n('scanned') <= total;
		const checked = share
			? `${n('scanned').toLocaleString()} of ${countWords(total, 'item')} checked`
			: `${countWords(n('scanned'), 'item')} checked so far`;
		const verb = kind === 'sync' ? 'Applying the rule' : (kind === 'pause' ? 'Pausing the rule' : 'Stopping the rule');
		textOut = `${verb}: ${checked}${joined ? `; ${joined}` : ''}.`;
	} else if (kind === 'sync') {
		textOut = joined ? `Rule applied: ${joined}.` : 'Rule applied. Nothing needed to change.';
	} else if (kind === 'pause') {
		textOut = joined ? `Rule paused: ${joined}. Tags people put on by hand stay.` : 'Rule paused. It had not tagged any items.';
	} else {
		textOut = joined ? `Stopped applying by rule: ${joined}.` : 'Stopped applying by rule. The rule had not tagged any items.';
	}
	if (stopped && !running) textOut = `${textOut} It stopped before the end: the rule changed, or the collection did.`;
	if (!running && fullCount) {
		textOut = `${textOut} ${countWords(fullCount, 'item')} already had ${RULE_LIMITS.handTags} tags, so ${fullCount === 1 ? 'it' : 'they'} went without this one.`;
	}
	const tone = running ? 'info' : (stopped || fullCount ? 'warning' : 'success');
	return { kind, running, waiting: null, tone, text: textOut, full, fullCount };
}

// ------------------------------------------------------------------ refusals

/**
 * Which part of the editor a refusal belongs to: `{ section, index, key }` —
 * `section` 'card' | 'where' | 'match' | 'strict' | 'related' | null (the
 * top), `index` the related clause, `key` its field ('card_id', 'link',
 * 'filter', 'window', 'of', 'op', 'value', 'whenNone').
 */
export function ruleErrorTarget(error) {
	const data = error?.response?.data || {};
	const tagCodes = ['private_tag_not_allowed', 'unknown_tag', 'ambiguous_tag'];
	const field = typeof data.field === 'string' ? data.field : '';
	if (data.code !== 'invalid_rule' && !tagCodes.includes(data.code)) return { section: null };
	const m = /^related\[(\d+)\](?:\.(\w+))?/.exec(field);
	if (m) return { section: 'related', index: Number(m[1]), key: m[2] || null };
	if (field === 'card_id') return { section: 'card' };
	if (field === 'where') return { section: 'where' };
	if (field === 'match') return { section: 'match' };
	if (field === 'strict') return { section: 'strict' };
	// A tag named in a filter (the API names `where` or a clause's filter in
	// its message, not always in `field`): the item's filter, unless said.
	if (tagCodes.includes(data.code)) return { section: 'where' };
	return { section: null };
}

// Codes whose API sentence names the wire (dryRun, confirm, a field's
// spelling) get words of ours; the rest keep the API's own, which is written
// for people.
const RULE_WORDS = {
	invalid_tag: 'Only a collection tag that isn’t restricted can be applied by a rule.',
	private_tag_not_allowed: 'This filter names a private tag. A rule runs for everyone, so it can only use the collection’s tags. Pick a collection tag instead.',
	unknown_tag: 'This filter names a tag that isn’t one of this collection’s tags any more. Pick the tag again.',
	ambiguous_tag: 'This filter names a tag by a name more than one tag has. Pick the one you mean from the list.',
	invalid_tag_ids: 'Pick at least one tag (at most 50).',
	collection_archived: 'This collection, or a sub-collection holding items with this tag, is archived, so nothing changed. Unarchive it first.',
	restricted_tag: 'A restricted tag is put on and taken off by managers, by hand. A rule can’t add or remove it.',
	rule_owned_tag: 'A rule applies this tag, and only that rule does. Nothing else can put it on or take it off.',
	rule_pass_running: 'This tag is still being applied from its last change. Try again when that finishes.',
	rule_changed: 'Someone else changed this tag’s rule while you were editing it. Close this, open it again to see their change, then make yours.',
	legacy_tags_present: 'Tags aren’t available right now. Try again in a little while.',
};

// `invalid_rule` names the part it refused; where the API's sentence spells
// the wire, the part's own words are said instead.
const FIELD_WORDS = {
	card_id: 'Choose one of this collection’s cards: the rule tags its items.',
	match: 'Choose whether every part must hold, or any one of them.',
	strict: 'Choose who applies this tag: only this rule, or people too.',
	'related.card_id': 'Choose one of this collection’s cards for the related items.',
	'related.link': 'Choose the item lookup on the related card that points at the rule’s card.',
	'related.aggregate': 'Choose what to take from the related items.',
	'related.op': 'Choose a comparison this value takes.',
	'related.whenNone': 'Choose what happens when there are no related items.',
	related: `A rule has at most ${RULE_LIMITS.related} sets of related items.`,
};

/** "in 12 minutes", "in 40 seconds" — a 429's `retryAfter` (seconds) as words. */
export function retryWords(seconds) {
	const n = Math.max(1, Math.ceil(Number(seconds) || 0));
	if (n < 60) return `in ${n} second${n === 1 ? '' : 's'}`;
	const minutes = Math.ceil(n / 60);
	return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * A rule write's refusal as the sentence a person reads (`count` for
 * `confirm_required`). Every code the rule routes answer has its words here.
 * A 429 from the pause / resume switch's own budget (ruled d34: six switches
 * an hour per rule) says so in its words; the API marks that budget
 * `budget: 'switch'` (the tags rulings — every route limiter's 429 carries a
 * `limit` too now, so the number alone no longer tells them apart).
 */
export function tagRuleRefusal(error, { fallback = 'The rule could not be saved. Please try again.' } = {}) {
	if (!error?.response) return 'We couldn’t reach the server. Check your connection and try again.';
	const status = error.response.status;
	const data = error.response.data || {};
	const code = data.code || null;
	if (status === 404) return 'This tag is not there any more. It may have been deleted.';
	if (status === 429) {
		const when = data.retryAfter ? `Try again ${retryWords(data.retryAfter)}.` : 'Wait a little, then try again.';
		if (data.budget === 'switch') return `This rule has been paused or resumed ${Number(data.limit) || 6} times this hour, the most it can be. ${when}`;
		return `That is a lot of changes to this rule in a short time. ${when}`;
	}
	if (status === 403 && !code) return 'Only managers of this collection can change its automatic tags.';
	if (code === 'confirm_required') {
		const count = Number(data.count) || 0;
		return `Only this rule will apply this tag, so it comes off the ${countWords(count, 'item')} people tagged by hand. Confirm that first.`;
	}
	if (code === 'invalid_rule') {
		const field = typeof data.field === 'string' ? data.field : '';
		const generic = field.replace(/^related\[\d+\]/, 'related').replace(/\.(\w+)\..*$/, '.$1');
		return FIELD_WORDS[generic] || (typeof data.message === 'string' && data.message) || 'The rule can’t be saved as it is. Check the part that is marked.';
	}
	if (RULE_WORDS[code]) return RULE_WORDS[code];
	return (typeof data.message === 'string' && data.message) || fallback;
}

/**
 * An automation's refusal over the tags it names (a `tagged` / `untagged`
 * trigger's `trigger.tagIds`, an `update-items` action's Add tag / Remove
 * tag) as plain words — or null when the refusal is about something else
 * (the editor then says the API's own sentence, as it always has).
 */
export function automationTagRefusal(error) {
	const data = error?.response?.data || {};
	switch (data.code) {
		case 'invalid_tag_ids':
			return 'Pick at least one tag for Tagged or Untagged, and at most 50.';
		case 'unknown_tag':
			return 'A tag this rule names isn’t one of this collection’s tags any more, or you can’t see it. Pick the tags again.';
		case 'restricted_tag':
		case 'rule_owned_tag':
			return RULE_WORDS[data.code];
		default:
			return null;
	}
}

/** Why a collection tag can't be added or removed by an automation's action, or null (API validateActionTags). */
export function actionTagBlockedReason(tag) {
	if (!tag) return null;
	if (tag.restricted) return 'Restricted: only managers put it on and take it off, by hand.';
	if (isStrictTag(tag)) return 'Only its own rule applies this tag.';
	return null;
}

/** Why a tag can't be applied by a rule — its reason for the ⋯ action — or null. */
export function ruleBlockedReason(tag) {
	if (!tag) return 'Choose a tag.';
	if (tag.owner?.kind !== 'collection') return 'Only a collection tag can be applied by a rule.';
	if (tag.restricted) return 'A restricted tag is put on by managers, by hand, so it can’t have a rule.';
	return null;
}

/** Is this collection tag applied by a rule? (`rule: { automation_id, strict, enabled }` on the tag.) */
export const isAutomaticTag = tag => Boolean(tag?.rule && tag.rule.automation_id);
/**
 * Is its rule paused? The vocabulary and the rule's answers carry
 * `rule.enabled` (keepr-api T6b, derived from the rule's automation); only a
 * `false` says paused — a tag read before T6b says nothing, and is not.
 */
export const isPausedTag = tag => isAutomaticTag(tag) && tag.rule.enabled === false;
/** Does only its rule apply it ("Only this rule applies this tag")? */
export const isStrictTag = tag => isAutomaticTag(tag) && tag.rule.strict === true;

/** The words for who applies a tag: the strict choice's two sides (ruled d11, d11b). */
export const STRICT_WORDS = Object.freeze({
	strict: 'Only this rule applies this tag',
	shared: 'People can apply it too',
});

// ------------------------------------------------------------------ time

// Where the zone of a rule's daily check comes from (keepr-api, the tags
// rulings b65: `daily.timezoneSource`) — the collection's own time zone, the
// nearest collection above it that sets one, or keepr's default.
const ZONE_SOURCE_WORDS = {
	collection: 'the collection’s time zone',
	parent: 'the time zone of the collection above it',
	platform: 'keepr’s default, as no time zone is set on the collection or above it',
};

/**
 * When a rule's daily check runs, in words: "just after midnight,
 * America/Los_Angeles time (the collection’s time zone)". Since keepr-api's
 * tags rulings (b65) `daily.at` is a wall time just after midnight
 * (00:05–01:04) in `daily.timezone`, the COLLECTION's time zone
 * (`timezoneSource`: 'collection' | 'parent' | 'platform'), so the words say
 * the zone and where it comes from rather than a time on the person's clock
 * — a check at midnight in Los Angeles is 9 in the morning in Paris.
 * ⚑ The wording. Null without a `daily`.
 */
export function dailyCheckWords(daily) {
	if (!daily || typeof daily !== 'object') return null;
	const zone = typeof daily.timezone === 'string' && daily.timezone.trim() ? daily.timezone.trim() : null;
	const source = ZONE_SOURCE_WORDS[daily.timezoneSource] || null;
	if (!zone) return 'just after midnight in the collection’s time zone';
	return `just after midnight, ${zone} time${source ? ` (${source})` : ''}`;
}

/**
 * What a TIME-DEPENDENT rule (a date comparison, a window, a relative date)
 * promises, in the person's terms (keepr-api T6b, ruled d18): it checks an
 * item whenever it or an item linked to it changes, and every item once a
 * day — just after midnight in the collection's time zone, named with where
 * it comes from (dailyCheckWords, b65) — and the next check (`nextRunAt`) as
 * a date and time on the person's own clock. Before the rule is saved there
 * is no time yet; while it is paused it checks nothing.
 */
export function timeDependentNote({ daily = null, enabled = true, now = new Date() } = {}) {
	const lead = 'This rule depends on dates.';
	const onChange = 'keepr checks an item again whenever it, or an item linked to it, changes';
	if (!daily) return `${lead} ${onChange}. Once the rule is saved, it also checks every item once a day.`;
	const when = `once a day, ${dailyCheckWords(daily)}`;
	if (!enabled) {
		return `${lead} While it is paused, keepr checks nothing. Once it is on again, ${onChange}, and checks every item ${when}.`;
	}
	const next = daily.nextRunAt ? dayjs(daily.nextRunAt) : null;
	let nextWords = '';
	if (next && next.isValid()) {
		nextWords = next.valueOf() <= now.getTime() ? ' The next check is due now.' : ` The next check is ${formatDisplayDateTime(next.toISOString())} your time.`;
	}
	return `${lead} ${onChange}, and checks every item ${when}.${nextWords}`;
}
